import {Helmet} from '@dr.pogodin/react-helmet';
import {Checkbox, openDeleteDialog} from 'platform/components';
import {Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useAccGetConfigurationInvoiceCostItemPlusQuery,
  useAccPatchConfigurationInvoiceCostItemPlusMutation,
  useDeleteConfigurationMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function InvoiceCostOverview() {
  const navigate = useNavigate();
  const [deleteConfiguration] = useDeleteConfigurationMutation();

  const {data: invoiceCostSubcontracts, isLoading: isLoadingSubcontracts} =
    useAccGetConfigurationInvoiceCostItemPlusQuery();
  const [updateInvoiceCostSubcontracts, {isLoading: isUpdatingInvoiceCostSubcontracts}] =
    useAccPatchConfigurationInvoiceCostItemPlusMutation();

  const onCheckboxChange = (checkboxValue: boolean) => {
    updateInvoiceCostSubcontracts({
      include: checkboxValue,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDeleteProcessingItem = (id: string, refreshData: () => void) =>
    openDeleteDialog({
      onConfirm: () =>
        deleteConfiguration({id})
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch(handleApiError),
      text: i18n.t('entity.accounting.labels.deleteInvoiceCostItemText'),
    });

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'edit', () =>
        navigate(composePath(settingsRoutes.invoiceCostDetail, {params: {id}}))
      )
      .with('delete', () => handleDeleteProcessingItem(id, refreshData));
  };

  const title = i18n.t('page.accountingSettings.labels.invoiceCostGeneration');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          actions: [
            {
              type: 'button',
              title: i18n.t('page.accounting.labels.newDefinition'),
              onClick: () => navigate(settingsRoutes.invoiceCostNew),
            },
          ],
        }}
        description={i18n.t('page.accountingSettings.labels.invoiceCostGenerationDescription')}
        data-testid={testIds.settings.invoiceCostOverview('page')}
      >
        <Checkbox
          isDisabled={isLoadingSubcontracts || isUpdatingInvoiceCostSubcontracts}
          value={invoiceCostSubcontracts?.include ?? false}
          onChange={onCheckboxChange}
          label={i18n.t(
            'page.accountingSettings.labels.invoiceCostGeneration.includeServiceSubcontracts.checkboxLabel'
          )}
          data-testid={testIds.settings.invoiceCostOverview('subcontractCheckbox')}
        />
        <Space vertical={8} />
        <DmsDataGrid
          gridCode="invoice-cost-item"
          actionCallback={actionCallback}
          data-testid={testIds.settings.invoiceCostOverview('data-grid')}
        />
      </SettingsTemplate>
    </>
  );
}
